import {
  Box,
  Breadcrumbs, Button, Dialog, DialogContent, DialogTitle, Fab,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import React, {useEffect, useState, Dispatch, SetStateAction} from "react";
import EmployeeAttendance from "./components/EmployeeAttendance";
import DeviceList from "./components/DeviceList";
import PresentStatus from "./components/PresentStatus";
import CustomBreadcrumbs from "@/components/breadcrumbs/CustomBreadcrumbs";
import {useTranslation} from "react-i18next";
import {IconButton, styled} from "@mui/material";
import AttendanceRecord from "./components/AttendanceRecord";
import AttendanceStatus from "./components/AttendanceStatus";
import PieChart from "./components/PieChart";
import PieChartBottom from "./components/PieChartBottom";
import ActionButton from "@/components/actions/ActionButton";
import {TitleTypography} from "./components/commonCmp";
import {useDispatch} from "react-redux";
import {setBreadcrumbs} from "@/store/slices/Breadcrumbs";
import CompanyService from "@/services/auth/CompanyService";
import ControlAutoComplete from "@/components/hook-form/ControlAutoComplete";
import {object, string, TypeOf, z} from "zod";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod/dist/zod";
import CountryService from "@/services/cis/CountryService";
import {SavingButton} from "@/components/data-grid/CustomButton";
import SnackbarUtil from "@/utils/SnackbarUtil";

const CustomGrid = styled(Grid)(({theme}) => ({
  backgroundColor: "#ffffff",
}));

const Item = styled(Grid)(({theme}) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
  backgroundColor: "#ffffff",
  height: "100%",
  borderRadius: "10px"
}));

const Dashboard: React.FC = () => {
  const {t, i18n} = useTranslation();
  const dispatch = useDispatch();
  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState<number>(0);
  const [refresh, setRefresh] = useState<number>(0);
  const [refreshChart, setRefreshChart] = useState<number>(0);
  const [popUp, setPopUp] = useState<boolean>(true);
  const [country, setCountry] = React.useState<string>('');
  const [countryList, setCountryList] = useState<DictOption[]>([])
  const [isLoading, setLoading] = useState<boolean>(false);

  const refreshChartData = () => {
    setRefreshChart(refreshChart + 1);
  };

  useEffect(() => {
    dispatch<any>(
      setBreadcrumbs<any>([{
        path: '',
        label: t('common.Dashboard')
      }])
    );
  }, [i18n.language]);

  interface DictOption {
    id: string;
    name: string;
  }

  const dashboardSchema = object({
    country: z.string().min(1, t("common.This field is required!")).max(120, t("common.Max length of name is 120")),
  })

  type dashboardInput = TypeOf<typeof dashboardSchema>;

  const dashboardForm = useForm<dashboardInput>({
    resolver: zodResolver(dashboardSchema),
    defaultValues: {
      country: '',
    }
  })

  useEffect(() => {
    CompanyService.countryCompany({'method': "get"}).then((res) => {
      if (res.status < 400) {
        if (!res.data?.country_value) {
          setPopUp(false)
        }
      }
    })
    CountryService.getCountryList()
      .then((result) => {
        const countries = Array.isArray(result?.data?.data?.countries)
          ? result.data.data.countries
          : [];

        const options = countries.map((country: any, index: number) => {
          return {
            id: country.iso31661Cd2,
            name: country.name,
          };
        });

        setCountryList(options);
      })
      .catch((error) => {
        console.error('获取国家列表失败：', error);
        setCountryList([]);
      });
  }, [])
  const onSubmit = () => {
    setLoading(true)
    CompanyService.countryCompany({'method': "post", 'country': country}).then((res) => {
      if (res.status < 400) {
        SnackbarUtil.success(t('common.success'));
        setPopUp(true)
      }
    })
    setLoading(false)
  }

  return (
    <Grid>
      <Grid
        sx={{
          display: "flex",
          flexWrap: "wrap",
          paddingRight: "14px",
          // height: "calc(50vh - 40px)",
          minHeight: "440px",
        }}
      >
        <Grid
          sx={{
            flexGrow: "1",
            minWidth: "500px",
            width: "65%",
            padding: "6px 6px 6px 0px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#ffffff",
            backgroundClip: "content-box",
            minHeight: "440px",
            borderRadius: "20px !important"
          }}
        >
          <Grid
            sx={{
              height: "40px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0px 8px",
              marginLeft: "5px",
            }}
          >
            <TitleTypography>{t("common.Attendance")}</TitleTypography>

            <Tooltip title={t("common.refresh")}>
              <div>
                <ActionButton
                  icon="icon-ic_refresh"
                  handleClick={refreshChartData}
                ></ActionButton>
              </div>
            </Tooltip>
          </Grid>
          <Grid
            sx={{
              flexGrow: "1",
            }}
          >
            <Item
              sx={{
                display: "flex",
                justifyContent: "space-around",
                alignItems: "center",
                width: "100%",
              }}
            >
              <PieChart
                refreshChart={refreshChart}
                category={"day"}
                title={t("common.Today")}
              ></PieChart>
              <PieChart
                refreshChart={refreshChart}
                category={"week"}
                title={t("common.This Week")}
              ></PieChart>
              <PieChart
                refreshChart={refreshChart}
                category={"month"}
                title={t("common.This Month")}
              ></PieChart>
            </Item>
          </Grid>
          <Grid
            sx={{
              height: "60px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <PieChartBottom refreshChart={refreshChart}></PieChartBottom>
          </Grid>
        </Grid>
        <Grid
          sx={{
            minWidth: "300px",
            flexGrow: "1",
            width: "30%",
            padding: "6px",
            boxSizing: "border-box",
            minHeight: "440px",
            height: "calc(50vh - 20px)",
          }}
        >
          <Item>
            <DeviceList/>
          </Item>
        </Grid>
      </Grid>

      <Grid
        sx={{
          display: "flex",
          flexWrap: "wrap",
          paddingRight: "14px",
          height: "calc(50vh - 20px)",
          minHeight: "440px",
          marginBottom: '14px'
        }}
      >
        <Grid
          sx={{
            flexGrow: "1",
            minWidth: "400px",
            width: "50%",
            padding: "6px 6px 6px 0px",
            boxSizing: "border-box",
            minHeight: "440px",
          }}
        >
          <Item>
            <AttendanceRecord
              pageSize={pageSize}
              setPageSize={setPageSize}
              page={page}
              setPage={setPage}
              refresh={refresh}
              setRefresh={setRefresh}
            ></AttendanceRecord>
          </Item>
        </Grid>
        <Grid
          sx={{
            minWidth: "400px",
            flexGrow: "1",
            width: "50%",
            padding: "6px",
            boxSizing: "border-box",
            minHeight: "440px",
            height: "calc(50vh - 20px)",
          }}
        >
          <Item>
            <AttendanceStatus
              pageSize={pageSize}
              setPageSize={setPageSize}
              page={page}
              setPage={setPage}
              refresh={refresh}
              setRefresh={setRefresh}
            ></AttendanceStatus>
          </Item>
        </Grid>
      </Grid>
      <Dialog
        open={!popUp}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '90%',
            maxWidth: '600px',
            height: '240px',
            position: 'relative'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 24px',
            backgroundColor: "transparent",
            // borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Typography variant="h6" sx={{fontWeight: 'bold'}}>
            {t("common.Confirm Your Region Settings")}
          </Typography>
        </DialogTitle>
        <Typography sx={{fontWeight: 'bold', padding: '8px 24px', fontSize: '14px', marginTop: "-10px", color: "#999999"}}>
          {t("common.We've updated our time and attendance rules. Please confirm your region.")}
        </Typography>
        <DialogContent sx={{padding: '8px 24px', position: 'relative'}}>
          <Box sx={{width: '100%', height: '30px'}}>
            <ControlAutoComplete
              name={'select_country'}
              form={dashboardForm}
              label={t("signUp.Country or Region")}
              required
              options={countryList}
              isOptionEqualToValue={(option: DictOption, value: DictOption | string) => {
                const targetValue = typeof value === 'object' ? value.name : value;
                return option.name === targetValue;
              }}
              onChange={(e) => {
                setCountry(e?.name)
              }}
            />
          </Box>
        </DialogContent>
        <Grid sx={{display: 'flex', justifyContent: 'start', padding: '8px 24px'}}>
          <SavingButton
            loading={isLoading}
            children={t("common.confirm")}
            onClick={onSubmit}
            sx={{width: '100%', textTransform: "none" }}
          />
        </Grid>
      </Dialog>
    </Grid>
  );
};
export default Dashboard;
